import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"

// components
import DataItem from "app/static/frontend/profiles-new/components/DataTableView/DataItem"
import SegueLink from "app/static/frontend/middleware/SegueLink"

// helpers
import { getClasses, getDlcClasses } from "profiles-new/helperFunctions"

// css
import "profiles-new/components/OverviewPage/index.styl"
import "profiles-new/components/DataTableView/index.styl"

import { DTV_STYLE_CONSTANTS } from "shared/profiles/constants/constants"
import { SEGUE_TO_DOCUMENTS_TEXT } from "shared/imports/sharedConstants"
import { DTVElement, DTVCard, Icon } from "quorumdesign"
import { DTVCard as DTVRawCard } from "app/static/frontend/profiles-new/components/DTVCard/DTVCard"

export const DataItemWrapper = ({
    title,
    subtitle,
    collapseEmptySpace,
    displayValue,
    onClick,
    width,
    isLink,
    tooltipText,
    dlcWidth,
    segueUrl,
    // Parent width is in units of Bootrap Columns
    parentWidth = 9, // default: 9 out of 12 Bootstrap Columns (ex: col-md-9)
    visible,
    // The style for the data items in this section now needs to be specified
    // by the section mapping these data items;
    style = DTV_STYLE_CONSTANTS.MINIMAL,
    // The direct children of design system DTVGrids are passed this prop
    // Since we are wrapping the DTVElement, which relies on this prop, forward it
    gridWidth,
    elementId,
    externalHyperlinkURL,
    truncateText,
}) => {
    // Adjust cell size based on sensible maximums for character
    // length and the proportion of the table that the item's
    // parent component occupies. 'parentWidth' is the number
    // of column's in bootstrap's 12 column layout.
    const SM_CHAR_WIDTH = 54 * (parentWidth / 12)
    const MD_CHAR_WIDTH = 117 * (parentWidth / 12)
    const LG_CHAR_WIDTH = 180 * (parentWidth / 12)

    const extraSubtitleClass = classNames({
        // if the subtitle is set to width 12 or the length is >= LG_CHAR_WIDTH, wrap the text
        "wrap-text": (!width && subtitle && subtitle.length >= LG_CHAR_WIDTH) || width === 12,
        // if it is < LG_CHAR_WIDTH chars and the width is not 12
        "has-overflow": (!width && subtitle && subtitle.length < LG_CHAR_WIDTH) || width !== 12,
        "is-blank-subtitle": subtitle === "N/A",
    })

    const props = {
        width,
        subtitle: displayValue || subtitle,
        sm: SM_CHAR_WIDTH,
        md: MD_CHAR_WIDTH,
        lg: LG_CHAR_WIDTH,
    }

    if (!dlcWidth) {
        switch (style) {
            case DTV_STYLE_CONSTANTS.CARD:
                return (
                    <DTVCard
                        title={title}
                        text={subtitle}
                        onClick={onClick}
                        // for now, it seems only committees use this DTV style, but
                        // these might need to be changed to come from the section data objects
                        // (we could pass the QDT from the section data object to the DataItem/Wrapper)
                        icon="gavel"
                        textIcon="podium"
                        gridWidth={gridWidth}
                    />
                )
            case DTV_STYLE_CONSTANTS.RAW_CARD:
                return <DTVRawCard segueUrl={segueUrl} title={title} />
            case DTV_STYLE_CONSTANTS.ELEMENT:
                return (
                    <DTVElement
                        // "See Documents" is sent as the subtitle for social media DTV items,
                        // unless they are twitter items, in which the twitter handle is the subtitle
                        // the "See Documents" subtitle is redundant with the button, so ignore it
                        text={subtitle !== SEGUE_TO_DOCUMENTS_TEXT && subtitle}
                        title={title}
                        subtitle={segueUrl ? <SegueLink to={segueUrl}>{displayValue}</SegueLink> : displayValue}
                        button={
                            onClick && {
                                leftIcon: "search",
                                onClickFn: onClick,
                                text: SEGUE_TO_DOCUMENTS_TEXT,
                            }
                        }
                        gridWidth={gridWidth}
                        collapseEmptySpace={collapseEmptySpace}
                        elementId={elementId}
                    />
                )
            case DTV_STYLE_CONSTANTS.MINIMAL:
            default:
                return (
                    <DTVElement
                        isMinimal
                        // "See Documents" is sent as the subtitle for social media DTV items,
                        // unless they are twitter items, in which the twitter handle is the subtitle
                        // the "See Documents" subtitle is redundant with the button, so ignore it
                        style={{ display: visible ? "initial" : "none" }}
                        text={
                            externalHyperlinkURL ? (
                                <a href={externalHyperlinkURL} target="_blank" rel="noreferrer">
                                    {subtitle}
                                    <Icon
                                        icon="external-link"
                                        iconFamily="far"
                                        size="sm"
                                        style={{ marginLeft: "5px" }}
                                    />
                                </a>
                            ) : (
                                subtitle !== SEGUE_TO_DOCUMENTS_TEXT && subtitle
                            )
                        }
                        title={title}
                        button={
                            onClick && {
                                leftIcon: "search",
                                onClickFn: onClick,
                                text: SEGUE_TO_DOCUMENTS_TEXT,
                            }
                        }
                        gridWidth={gridWidth}
                        elementId={elementId}
                        truncateText={truncateText}
                    />
                )
        }
    }

    if (isLink) {
        return (
            <a
                className={`${getClasses(props)} dataItem width-${width}`}
                href={`${title === "Email" ? "mailto:" : ""}${subtitle}`}
                target={title !== "Email" ? "_blank" : ""}
                style={{ display: visible ? "initial" : "none" }}
                rel="noreferrer"
            >
                <DataItem
                    title={title}
                    subtitle={title === "Website" ? "Visit Website" : displayValue || subtitle}
                    onClick={onClick}
                    isLink={isLink}
                    tooltipText={tooltipText}
                    extraSubtitleClass={extraSubtitleClass}
                />
            </a>
        )
    } else if (segueUrl) {
        return (
            <SegueLink
                className={`${getClasses(props)} dataItem width-${width}`}
                to={segueUrl}
                style={{ display: visible ? "initial" : "none" }}
            >
                <DataItem
                    title={title}
                    subtitle={title === "Website" ? "Visit Website" : displayValue || subtitle}
                    onClick={onClick}
                    isLink={isLink}
                    tooltipText={tooltipText}
                    extraSubtitleClass={extraSubtitleClass}
                />
            </SegueLink>
        )
    } else {
        const a11yProps = { ...(onClick ? { onClick } : {}) }
        return (
            <div
                className={
                    dlcWidth
                        ? `${getDlcClasses({ title, subtitle, width: dlcWidth })} dataItem width-${width}`
                        : `${getClasses(props)} dataItem width-${width}`
                }
                style={{ display: visible ? "initial" : "none" }}
                {...a11yProps}
            >
                <DataItem
                    title={title}
                    subtitle={subtitle}
                    tooltipText={tooltipText}
                    extraSubtitleClass={extraSubtitleClass}
                    dlcWidth={dlcWidth}
                />
            </div>
        )
    }
}

DataItemWrapper.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    displayValue: PropTypes.string,
    onClick: PropTypes.func,
    width: PropTypes.number,
    isLink: PropTypes.bool,
    tooltipText: PropTypes.string,
    parentWidth: PropTypes.number,
    visible: PropTypes.bool,
    dlcWidth: PropTypes.number,
    style: PropTypes.string, // one of the constants from profiles-new/constants::DTV_STYLE_CONSTANTS
    collapseEmptySpace: PropTypes.bool,
    externalHyperlinkURL: PropTypes.string,
}

export default DataItemWrapper
